import { ref, watch } from '@vue/composition-api'
import axios from '@axios'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import pushStateFiltersParams from '@/helpers/pushStateFiltersParams'
import store from '@/store'
import { DEFAULT_PER_PAGE, PER_PAGE_OPTIONS } from '@/constants/base'

export default function useApplicationsList(root) {
  // Use toast
  const toast = useToast()

  const refEmailAuditListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'recipient', label: 'Recipient Email', sortable: true },
    { key: 'subject', label: 'subject', sortable: true },
    { key: 'user_type', label: 'User Type', sortable: true },
    { key: 'trigger', label: 'trigger', sortable: true },
    { key: 'timestamp', label: 'timestamp', sortable: true },
  ]

  const defaultPerPage = Number(localStorage.getItem('emailAuditPerPage'))
  const perPage = ref(defaultPerPage || DEFAULT_PER_PAGE)
  const totalItems = ref(0)
  const currentPage = ref(1)
  const perPageOptions = PER_PAGE_OPTIONS
  const searchQuery = ref('')
  const filterTimestamp = ref('')
  const filterTrigger = ref('')
  const filterUserType = ref('')
  const sortBy = ref('created_at')
  const isSortDirDesc = ref(true)
  let debounceTimeout = ref(null)

  const dataMeta = ref({
    from: 0,
    to: 0,
    of: 0,
  })

  const clearFilters = () => {
    filterTimestamp.value = ''
    filterTrigger.value = ''
    filterUserType.value = ''
  }

  const refetchData = () => {
    refEmailAuditListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, filterTimestamp, filterTrigger, filterUserType],
    ([currentPageVal, perPageVal, searchQueryVal, filterTimestampVal, filterTriggerVal, filterUserTypeVal],
      [currentPagePrevVal, perPagePrevVal, searchQueryPrevVal, filterTimestampPrevVal, filterTriggerPrevVal, filterUserTypePrevVal]) => {
      if (currentPageVal !== currentPagePrevVal) {
        pushStateFiltersParams(root, 'admin-email-audit', 'currentPage', currentPageVal)
      }
      if (perPageVal !== perPagePrevVal) {
        pushStateFiltersParams(root, 'admin-email-audit', 'perPage', perPageVal)
      }
      if (searchQueryVal !== searchQueryPrevVal) {
        clearTimeout(debounceTimeout)
        debounceTimeout = setTimeout(() => {
          pushStateFiltersParams(root, 'admin-email-audit', 'searchQuery', searchQueryVal)
        }, 250)
      }
      if (filterTimestampVal !== filterTimestampPrevVal) {
        pushStateFiltersParams(root, 'admin-email-audit', 'filterTimestamp', filterTimestampVal)
      }
      if (filterTriggerVal !== filterTriggerPrevVal) {
        pushStateFiltersParams(root, 'admin-email-audit', 'filterTrigger', filterTriggerVal)
      }
      if (filterUserTypeVal !== filterUserTypePrevVal) {
        pushStateFiltersParams(root, 'admin-email-audit', 'filterUserType', filterUserTypeVal)
      }
      refetchData()
    })

  const fetchEmailAuditList = (ctx, callback) => {
    const payloadData = {
      perPage: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
    }
    if (filterTimestamp.value) {
      payloadData.createdAt = filterTimestamp.value
    }
    if (filterTrigger.value) {
      payloadData.trigger = filterTrigger.value
    }
    if (filterUserType.value) {
      payloadData.userType = filterUserType.value
    }
    if (searchQuery.value) {
      payloadData.search = searchQuery.value
    }
    store
      .dispatch('app-email-audit/fetchEmailAuditList', payloadData)
      .then(response => {
        const { data } = response.data
        const { total, from, to } = response.data.meta
        callback(data)
        totalItems.value = total
        dataMeta.value = {
          from,
          to,
          of: total,
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error fetching email audits' list",
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const resentEmail = ID => axios
    .get(`/auth/resend-email/${ID}`)
    .then(() => {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Email was sent successfully',
          icon: 'ThumbsUpIcon',
          variant: 'info',
        },
      })
    })
    .catch(() => toast({
      component: ToastificationContent,
      props: {
        title: 'Error sending email',
        icon: 'AlertTriangleIcon',
        variant: 'danger',
      },
    }))

  const fetchMailLogDetail = ID => axios
    .get(`/auth/mail-logs/${ID}`)
    .then(response => response.data.data)
    .catch(() => toast({
      component: ToastificationContent,
      props: {
        title: 'Error fetching mail log detail',
        icon: 'AlertTriangleIcon',
        variant: 'danger',
      },
    }))

  return {
    fetchEmailAuditList,
    tableColumns,
    perPage,
    currentPage,
    dataMeta,
    perPageOptions,
    searchQuery,
    filterTimestamp,
    filterTrigger,
    filterUserType,
    sortBy,
    totalItems,
    isSortDirDesc,
    refEmailAuditListTable,

    resentEmail,
    refetchData,
    clearFilters,
    fetchMailLogDetail,
  }
}
